import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Download, Filter, Search, ArrowLeft, BarChart3 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useLocation } from "wouter";

type MovementType = "ENTRADA" | "INTERNA" | "SAIDA" | "REPOSICAO";
type MovementStatus = "ABERTO" | "EM_ATENDIMENTO" | "CONCLUIDO" | "CANCELADO";

export default function History() {
  const { user, loading: authLoading } = useGoogleAuth();
  const [, setLocation] = useLocation();
  const [search, setSearch] = useState("");
  const [tipoFilter, setTipoFilter] = useState<MovementType | "ALL">("ALL");
  const [statusFilter, setStatusFilter] = useState<MovementStatus | "ALL">("ALL");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    if (!authLoading && !user) {
      window.location.href = "/login";
    }
  }, [user, authLoading]);

  const { data: movements = [], isLoading } = trpc.movements.list.useQuery(
    {
      search: search || undefined,
      tipo: tipoFilter !== "ALL" ? tipoFilter : undefined,
      status: statusFilter !== "ALL" ? statusFilter : undefined,
      startDate: startDate ? new Date(startDate) : undefined,
      endDate: endDate ? new Date(endDate) : undefined,
      limit: 500,
    },
    { enabled: !!user }
  );

  const exportCsvQuery = trpc.movements.exportCsv.useQuery(
    {
      search: search || undefined,
      tipo: tipoFilter !== "ALL" ? tipoFilter : undefined,
      status: statusFilter !== "ALL" ? statusFilter : undefined,
      startDate: startDate ? new Date(startDate) : undefined,
      endDate: endDate ? new Date(endDate) : undefined,
    },
    { enabled: false }
  );

  const handleExportExcel = async () => {
    try {
      const result = await exportCsvQuery.refetch();
      if (result.data?.excel) {
        // Converter base64 para blob
        const byteCharacters = atob(result.data.excel);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `movimentacoes_${new Date().toISOString().split("T")[0]}.xlsx`);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("Excel exportado com sucesso");
      }
    } catch (error) {
      toast.error("Erro ao exportar CSV");
    }
  };

  const getStatusColor = (status: MovementStatus) => {
    switch (status) {
      case "ABERTO":
        return "text-orange-600 bg-orange-50";
      case "EM_ATENDIMENTO":
        return "text-blue-600 bg-blue-50";
      case "CONCLUIDO":
        return "text-green-600 bg-green-50";
      case "CANCELADO":
        return "text-gray-600 bg-gray-50";
    }
  };

  const getTipoColor = (tipo: MovementType) => {
    switch (tipo) {
      case "ENTRADA":
        return "text-purple-600 bg-purple-50";
      case "INTERNA":
        return "text-blue-600 bg-blue-50";
      case "SAIDA":
        return "text-green-600 bg-green-50";
      case "REPOSICAO":
        return "text-amber-600 bg-amber-50";
    }
  };

  const formatStatus = (status: MovementStatus) => {
    return status.replace("_", " ");
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="sticky top-0 z-10 bg-card border-b shadow-sm">
        <div className="container py-4">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-foreground">Histórico</h1>
            <div className="flex gap-2">
              {/* Botão Dashboard (apenas líderes) */}
              {user && (user.role === "lider" || user.role === "admin") && (
                <Button
                  onClick={() => setLocation("/dashboard")}
                  size="sm"
                  variant="outline"
                  className="gap-2"
                >
                  <BarChart3 className="h-4 w-4" />
                  Dashboard
                </Button>
              )}
              <Button
                onClick={handleExportExcel}
                size="sm"
                variant="outline"
                className="gap-2"
                disabled={exportCsvQuery.isFetching}
              >
                <Download className="h-4 w-4" />
                {exportCsvQuery.isFetching ? "Exportando..." : "Exportar Excel"}
              </Button>
            </div>
          </div>

          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar produto, origem, destino..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Select
                value={tipoFilter}
                onValueChange={(v) => setTipoFilter(v as MovementType | "ALL")}
              >
                <SelectTrigger>
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Todos Tipos</SelectItem>
                  <SelectItem value="ENTRADA">Entrada</SelectItem>
                  <SelectItem value="INTERNA">Interna</SelectItem>
                  <SelectItem value="SAIDA">Saída</SelectItem>
                  <SelectItem value="REPOSICAO">Reposição</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={statusFilter}
                onValueChange={(v) => setStatusFilter(v as MovementStatus | "ALL")}
              >
                <SelectTrigger>
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Todos Status</SelectItem>
                  <SelectItem value="ABERTO">Aberto</SelectItem>
                  <SelectItem value="EM_ATENDIMENTO">Em Atendimento</SelectItem>
                  <SelectItem value="CONCLUIDO">Concluído</SelectItem>
                  <SelectItem value="CANCELADO">Cancelado</SelectItem>
                </SelectContent>
              </Select>

              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                placeholder="Data inicial"
              />

              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                placeholder="Data final"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="container py-4">
        <div className="bg-card rounded-lg border shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground">
                    ID
                  </th>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground border-l">
                    Status
                  </th>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground border-l">
                    Tipo
                  </th>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground border-l">
                    Produto
                  </th>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground border-l">
                    Origem
                  </th>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground border-l">
                    Destino
                  </th>

                  <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground border-l">
                    Referência
                  </th>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground border-l">
                    Nº Pedido
                  </th>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground border-l">
                    Criado
                  </th>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground border-l">
                    Concluído
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {isLoading ? (
                  <tr>
                    <td colSpan={10} className="text-center py-8 text-muted-foreground">
                      Carregando...
                    </td>
                  </tr>
                ) : movements.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center py-8 text-muted-foreground">
                      Nenhuma movimentação encontrada
                    </td>
                  </tr>
                ) : (
                  movements.map((movement, index) => (
                    <tr key={movement.id} className={`hover:bg-muted/30 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                      <td className="py-2 px-3 text-sm font-mono text-muted-foreground text-center">
                        #{movement.id}
                      </td>
                      <td className="py-2 px-3 text-center border-l">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(
                            movement.status
                          )}`}
                        >
                          {formatStatus(movement.status)}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-center border-l">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getTipoColor(
                            movement.tipo
                          )}`}
                        >
                          {movement.tipo}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-sm font-medium text-center border-l">
                        {movement.produto}
                      </td>
                      <td className="py-2 px-3 text-sm text-muted-foreground text-center border-l">
                        {movement.origem}
                      </td>
                      <td className="py-2 px-3 text-sm text-muted-foreground text-center border-l">
                        {movement.destino}
                      </td>

                      <td className="py-2 px-3 text-sm text-muted-foreground text-center border-l">
                        {movement.referencia || "-"}
                      </td>
                      <td className="py-2 px-3 text-sm text-muted-foreground text-center border-l">
                        {movement.numeroPedido || "-"}
                      </td>
                      <td className="py-2 px-3 text-xs text-muted-foreground text-center border-l">
                        {formatDistanceToNow(new Date(movement.criadoEm), {
                          addSuffix: true,
                          locale: ptBR,
                        })}
                      </td>
                      <td className="py-2 px-3 text-xs text-muted-foreground text-center border-l">
                        {movement.concluidoEm
                          ? formatDistanceToNow(new Date(movement.concluidoEm), {
                              addSuffix: true,
                              locale: ptBR,
                            })
                          : "-"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
